import type { SupportedLanguage } from '../../../utils/languageDetection';

export type SkillRunnerCopy = {
  welcomePaste: string;
  errorEmptyPaste: string;
  foundRequests: (count: number) => string;
  errorAnalyze: (msg: string) => string;
  askLocation: string;
  decideYourself: string;
  autoModeNotice: string;
  askChronological: string;
  askOuterClauseMatchYes: string;
  askOuterClauseMatchNo: string;
  appendAtLocationNotice: (locationLabel: string) => string;
  askFormat: string;
  chronologicalYes: string;
  chronologicalNo: string;
  formatTrackChanges: string;
  formatPlain: string;
  askAuthor: string;
  authorMyName: (name: string) => string;
  authorUnknown: string;
  authorSomeoneElse: string;
  askCustomAuthor: string;
  defaultAuthor: string;
  installing: string;
  doneSummary: (succeeded: number, failed: number) => string;
  noneInserted: string;
  errorExecute: (msg: string) => string;
  placeholderPaste: string;
  placeholderAuthor: string;
};

const COPY: Record<SupportedLanguage, SkillRunnerCopy> = {
  he: {
    welcomePaste: 'העתק/י והדבק/י את בקשות הלקוח מהמייל בתיבה למטה, ולחץ/י Enter לשליחה.',
    errorEmptyPaste: 'יש להדביק את בקשות הלקוח מהמייל.',
    foundRequests: (count) =>
      count === 1
        ? 'מצאתי בקשת לקוח אחת. היכן להתקין אותה?'
        : `מצאתי ${count} בקשות לקוח. היכן להתקין אותן?`,
    errorAnalyze: (msg) => `מצטער/ת, לא הצלחתי לנתח את התוכן שהודבק: ${msg}`,
    askLocation: '',
    decideYourself: 'תחליט בעצמך',
    autoModeNotice:
      'בסדר — אחליט/י לכל בקשה את המיקום המתאים. בקשות עם הפניה מפורשת לסעיף שבגוף המסמך יותקנו ליד הפסקה הרלוונטית; בקשות כלליות יועדפו תחת "כללי" / "הערות כלליות" אם קיים במסמך.',
    askChronological: 'האם להתקין את בקשות הלקוח לפי סדר כרונולוגי?',
    askOuterClauseMatchYes: 'כן, לפי ההפניות לסעיפים שבגוף המסמך',
    askOuterClauseMatchNo: 'לא, תתקין את הבקשות כהמשך ישיר של הסעיף האחרון',
    appendAtLocationNotice: (locationLabel) =>
      `בסדר — אתקין/ה את כל הבקשות בתחתית "${locationLabel}" כהמשך ישיר החל מהסעיף האחרון.`,
    askFormat: 'האם להתקין עם שינויי מעקב או כטקסט רגיל?',
    chronologicalYes: 'כן, לפי סדר כרונולוגי',
    chronologicalNo: 'כהמשך ישיר החל מהסעיף האחרון',
    formatTrackChanges: 'שינויי מעקב',
    formatPlain: 'טקסט רגיל',
    askAuthor: 'בשם מי יש לייחס את שינויי המעקב?',
    authorMyName: (name) => `בשמי: ${name}`,
    authorUnknown: 'Unknown',
    authorSomeoneElse: 'בשם מישהו אחר שאגדיר לך את שמו',
    askCustomAuthor:
      'מה שם הכותב שאותו אייחס ל"עקוב אחר שינויים" שאשתיל במסמך?',
    defaultAuthor: 'Unknown',
    installing: 'מתקין/ה את בקשות הלקוח במסמך…',
    doneSummary: (succeeded, failed) =>
      failed > 0
        ? `בוצע! הותקנו ${succeeded} בקשות לקוח (${failed} נכשלו).`
        : succeeded === 1
          ? 'בוצע! הותקנה בקשת לקוח אחת.'
          : `בוצע! הותקנו ${succeeded} בקשות לקוח.`,
    noneInserted: 'לא הותקנו בקשות לקוח. בדוק/י את המסמך ונסה/י שוב.',
    errorExecute: (msg) => `מצטער/ת, ההתקנה נכשלה: ${msg}`,
    placeholderPaste: 'הדבק/י בקשות לקוח מהמייל…',
    placeholderAuthor: 'למשל: שם הלקוח (השאר/י ריק ל-Unknown)',
  },
  en: {
    welcomePaste:
      'Copy and paste the client requests from the email into the box below, then press Enter to send.',
    errorEmptyPaste: 'Please paste the client requests from the email.',
    foundRequests: (count) =>
      `I found ${count} client request${count !== 1 ? 's' : ''}. Where should I install them?`,
    errorAnalyze: (msg) => `Sorry, I could not analyze the pasted content: ${msg}`,
    askLocation: '',
    decideYourself: 'Decide for me',
    autoModeNotice:
      'Got it — I will choose the best location for each request. Requests with an explicit reference to a clause in the document body go near the relevant paragraph; general requests prefer a "General" / "General notes" section when available.',
    askChronological: 'Should I install the client requests in chronological order?',
    askOuterClauseMatchYes: 'Yes, match clause references in the document body',
    askOuterClauseMatchNo: 'No, install as a direct continuation from the last clause',
    appendAtLocationNotice: (locationLabel) =>
      `Got it — I'll install all requests at the bottom of "${locationLabel}" as a direct continuation from the last clause.`,
    askFormat: 'Should I install with track changes or as plain text?',
    chronologicalYes: 'Yes, chronological order',
    chronologicalNo: 'As a direct continuation from the last clause',
    formatTrackChanges: 'Track changes',
    formatPlain: 'Plain text',
    askAuthor: 'On behalf of who should the track changes be attributed?',
    authorMyName: (name) => `In my name: ${name}`,
    authorUnknown: 'Unknown',
    authorSomeoneElse: 'Someone else (I will specify the name)',
    askCustomAuthor:
      'What author name should I use for the track changes I insert into the document?',
    defaultAuthor: 'Unknown',
    installing: 'Installing client requests into your document…',
    doneSummary: (succeeded, failed) =>
      succeeded > 0
        ? `Done! Inserted ${succeeded} client request${succeeded !== 1 ? 's' : ''}${failed > 0 ? ` (${failed} failed)` : ''}.`
        : 'No client requests were inserted.',
    noneInserted: 'No client requests were inserted. Please check the document and try again.',
    errorExecute: (msg) => `Sorry, installation failed: ${msg}`,
    placeholderPaste: 'Paste client requests from email…',
    placeholderAuthor: 'e.g. Client Name (leave empty for Unknown)',
  },
};

export function getSkillRunnerCopy(language: SupportedLanguage = 'he'): SkillRunnerCopy {
  return COPY[language] ?? COPY.he;
}
